/**
 * services/patient.service.ts — CLI-451 + CLI-452
 * API-функции кабинета пациента.
 */
import axios from 'axios';
import { api } from './api';
import { useAuthStore } from '../store/auth.store';
import type { PatientProfile } from '../store/auth.store';

// ── Auth (CLI-450) ─────────────────────────────────────────────────────────

export interface RequestCodeResult {
  ok: boolean;
  message: string;
  _devCode?: string;
}

export interface VerifyCodeResult {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  patient: PatientProfile;
}

export async function requestCode(phone: string, clinicId: string): Promise<RequestCodeResult> {
  const { data } = await axios.post<RequestCodeResult>('/api/patient/auth/request-code', { phone, clinicId });
  return data;
}

export async function verifyCode(phone: string, clinicId: string, code: string): Promise<VerifyCodeResult> {
  const { data } = await axios.post<{ data: VerifyCodeResult }>('/api/patient/auth/verify-code', { phone, clinicId, code });
  return data.data;
}

export async function logout(): Promise<void> {
  const { refreshToken } = useAuthStore.getState();
  if (refreshToken) {
    await axios.post('/api/patient/auth/logout', { refreshToken }).catch(() => null);
  }
  useAuthStore.getState().logout();
}

// ── Profile (CLI-450/451) ──────────────────────────────────────────────────

export interface PatientMe extends PatientProfile {
  phone: string | null;
  email: string | null;
  registrationStatus: string;
}

export async function getMe(): Promise<PatientMe> {
  const { data } = await api.get<{ data: PatientMe }>('/auth/me');
  return data.data;
}

// ── Documents (CLI-451) ───────────────────────────────────────────────────

export interface DocumentFile {
  id: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: number;
  status: string;
  createdAt: string;
}

export async function getDocuments(): Promise<DocumentFile[]> {
  const { data } = await api.get<{ data: DocumentFile[] }>('/documents');
  return data.data;
}

// ── Lab Results (CLI-451) ─────────────────────────────────────────────────

export interface LabResult {
  id: string;
  value: string | null;
  unit: string | null;
  referenceMin: number | null;
  referenceMax: number | null;
  aiFlag: string;
  isVerified: boolean;
  note: string | null;
  test: { id: string; code: string; name: string; unit: string | null };
}

export interface LabOrder {
  id: string;
  orderNumber: string;
  status: string;
  priority: string;
  sampleTakenAt: string | null;
  completedAt: string | null;
  createdAt: string;
  results: LabResult[];
}

export async function getLabOrders(): Promise<LabOrder[]> {
  const { data } = await api.get<{ data: LabOrder[] }>('/lab');
  return data.data;
}

// ── Studies (CLI-451) ─────────────────────────────────────────────────────

export interface ImagingStudy {
  id: string;
  type: string;
  status: string;
  indication: string | null;
  description: string | null;
  radiologistName: string | null;
  performedAt: string | null;
  describedAt: string | null;
  fileName: string | null;
  fileMime: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

export async function getStudies(): Promise<ImagingStudy[]> {
  const { data } = await api.get<{ data: ImagingStudy[] }>('/studies');
  return data.data;
}

// ── Notifications (CLI-451) ───────────────────────────────────────────────

export interface PatientNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  link: string | null;
  createdAt: string;
}

export async function getNotifications(): Promise<{ data: PatientNotification[]; meta: { unread: number } }> {
  const { data } = await api.get<{ data: PatientNotification[]; meta: { unread: number } }>('/notifications');
  return data;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/notifications/${id}/read`);
}

// ── Appointments (CLI-452) ────────────────────────────────────────────────

export interface PatientAppointment {
  id: string;
  date: string;
  durationMin: number;
  type: string;
  status: string;
  reason: string | null;
  room: { id: string; name: string } | null;
}

export async function getAppointments(): Promise<PatientAppointment[]> {
  const { data } = await api.get<{ data: PatientAppointment[] }>('/appointments');
  return data.data;
}

export interface BookingPayload {
  roomId: string;
  date: string;
  time: string;
  reason?: string;
  type?: 'consultation' | 'lab';
}

export async function bookAppointment(payload: BookingPayload): Promise<PatientAppointment> {
  const { data } = await api.post<{ data: PatientAppointment }>('/appointments', payload);
  return data.data;
}

export async function cancelAppointment(id: string): Promise<void> {
  await api.delete(`/appointments/${id}`);
}
